import { BarChart3, Brain, Clock, Gauge, LayoutGrid, Shapes, type LucideIcon } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import useTranslation from "../../client/hooks/useTranslation";
import { useLiteData } from "../data/LiteDataProvider";
import { EmptyStart } from "../onboarding/EmptyStart";
import { PageBody, PageHeader } from "../shell/LiteLayout";
import { cardCls, Empty } from "../ui/ui";
import { InstrumentsTab } from "./stats/InstrumentsTab";
import { OverviewTab } from "./stats/OverviewTab";
import { PsychoTab } from "./stats/PsychoTab";
import { RiskTab } from "./stats/RiskTab";
import { TimingTab } from "./stats/TimingTab";

type Tab = "overview" | "timing" | "instruments" | "risk" | "psycho";

const TABS: { id: Tab; icon: LucideIcon }[] = [
  { id: "overview", icon: LayoutGrid },
  { id: "timing", icon: Clock },
  { id: "instruments", icon: Shapes },
  { id: "risk", icon: Gauge },
  { id: "psycho", icon: Brain },
];

// Stats du Lite : 5 onglets rangés par question, onglet courant dans ?vue=.
export default function LiteStatsPage() {
  const tl = useTranslation().lite;
  const t = tl.stats;
  const { loading, trades, filteredTrades } = useLiteData();
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.find(x => x.id === params.get("vue"))?.id ?? "overview";

  const selectTab = (id: Tab) => {
    const next = new URLSearchParams(params);
    if (id === "overview") next.delete("vue"); else next.set("vue", id);
    setParams(next);
  };

  return (
    <>
      <PageHeader title={t.title} subtitle={t.subtitle} />
      <PageBody>
        <nav className="flex gap-1 overflow-x-auto [scrollbar-width:none] border-b border-border -mt-1" role="tablist">
          {TABS.map(x => {
            const active = x.id === tab;
            return (
              <button
                key={x.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => selectTab(x.id)}
                className={`flex items-center gap-2 px-3.5 h-11 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  active ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <x.icon size={16} className={active ? "text-primary" : ""} />{t.tabs[x.id].label}
              </button>
            );
          })}
        </nav>

        {loading ? (
          <div className={cardCls}><Empty title={tl.common.loading} /></div>
        ) : !trades.length ? (
          <EmptyStart icon={BarChart3} title={t.empty.title} text={t.empty.text} />
        ) : !filteredTrades.length ? (
          <div className={cardCls}><Empty title={tl.common.noTradeInPeriod}>{tl.common.noTradeInPeriodHint}</Empty></div>
        ) : (
          <div className="space-y-5" key={tab}>
            {tab === "overview" && <OverviewTab trades={filteredTrades} />}
            {tab === "timing" && <TimingTab trades={filteredTrades} />}
            {tab === "instruments" && <InstrumentsTab trades={filteredTrades} />}
            {tab === "risk" && <RiskTab trades={filteredTrades} />}
            {tab === "psycho" && <PsychoTab trades={filteredTrades} />}
          </div>
        )}
      </PageBody>
    </>
  );
}
